(() => {
  if (customElements.get('celvian-desktop-header')) return;
  class CelvianDesktopHeader extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      // Do not manage localization details here: Shopify's language form must remain open until the option is activated.
      this.menus = Array.from(this.querySelectorAll('details.cv-header__menu, details[data-cv-search]'));
      this.search = this.querySelector('[data-cv-search]');
      this.input = this.querySelector('[data-cv-search-input]');
      this.results = this.querySelector('[data-cv-results]');
      this.status = this.querySelector('[data-cv-search-status]');
      this.trending = this.querySelector('[data-cv-trending]');
      if (this.trending) this.trending.hidden = Boolean(this.input?.value.trim());
      this.section = this.closest('.cv-desktop-header-section');
      this.desktop = matchMedia('(min-width: 1025px)');
      this.cache = new Map();
      this.sequence = 0;
      this.lastScroll = window.scrollY;
      this.menus.forEach(menu => menu.addEventListener('toggle', () => {
        if (menu.open) {
          this.menus.forEach(other => { if (other !== menu) other.open = false; });
          if (menu === this.search) this.input?.focus();
        } else if (menu === this.search) this.cancelSearch();
        this.section?.classList.remove('cv-header-hidden');
      }, options));
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.close();
      }, options);
      this.addEventListener('focusout', () => {
        queueMicrotask(() => { if (!this.contains(document.activeElement)) this.close(); });
      }, options);
      this.addEventListener('keydown', event => this.onKeydown(event), options);
      this.querySelector('[data-cv-search-close]')?.addEventListener('click', () => {
        this.search.open = false;
        this.search.querySelector('summary').focus();
      }, options);
      if (this.input) {
        this.input.addEventListener('input', () => {
          this.cancelSearch();
          const query = this.input.value.trim();
          if (this.trending) this.trending.hidden = Boolean(query);
          this.results.replaceChildren();
          this.results.hidden = true;
          this.input.setAttribute('aria-expanded', 'false');
          this.status.textContent = '';
          if (query && this.dataset.predictive === 'true') {
            this.timer = setTimeout(() => this.suggest(query), 220);
          }
        }, options);
        this.input.form.addEventListener('submit', event => {
          const active = this.results.querySelector('[aria-selected="true"] a');
          if (active) { event.preventDefault(); active.click(); }
          else if (!this.input.value.trim()) event.preventDefault();
        }, options);
      }
      this.desktop.addEventListener('change', () => {
        this.close();
        this.section?.classList.remove('cv-header-hidden');
        this.updateHeight();
      }, options);
      window.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          const y = window.scrollY;
          this.classList.toggle('cv-header-scrolled', y > 8);
          const hide = this.dataset.sticky === 'scroll-up' && this.desktop.matches &&
            y > this.offsetHeight * 2 && y > this.lastScroll && !this.menus.some(menu => menu.open) &&
            !this.contains(document.activeElement);
          this.section?.classList.toggle('cv-header-hidden', hide);
          this.lastScroll = y;
        });
      }, { ...options, passive: true });
      window.addEventListener('pagehide', () => this.close(), options);
      this.observer = new ResizeObserver(() => this.updateHeight());
      this.observer.observe(this);
      this.updateHeight();
    }
    updateHeight() {
      if (this.desktop.matches) document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    }
    cancelSearch() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.sequence++;
      this.input?.removeAttribute('aria-activedescendant');
      this.results?.querySelectorAll('[aria-selected="true"]').forEach(item => item.setAttribute('aria-selected', 'false'));
    }
    close() {
      this.menus?.forEach(menu => { menu.open = false; });
      this.cancelSearch();
    }
    onKeydown(event) {
      if (event.key === 'Escape') {
        const menu = event.target.closest('details[open]');
        if (menu) {
          event.preventDefault();
          menu.open = false;
          menu.querySelector('summary').focus();
        }
        return;
      }
      if (event.target === this.input && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
        const items = Array.from(this.results.children);
        if (!items.length || this.results.hidden) return;
        event.preventDefault();
        const current = items.findIndex(item => item.getAttribute('aria-selected') === 'true');
        const next = event.key === 'ArrowDown' ? (current + 1) % items.length : (current < 0 ? items.length - 1 : (current - 1 + items.length) % items.length);
        items.forEach((item, index) => item.setAttribute('aria-selected', String(index === next)));
        this.input.setAttribute('aria-activedescendant', items[next].id);
        items[next].scrollIntoView({ block: 'nearest' });
      } else if (event.target.matches('.cv-header__menu > summary') && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        const menu = event.target.parentElement;
        menu.open = true;
        const links = menu.querySelectorAll('a[href]');
        links[event.key === 'ArrowDown' ? 0 : links.length - 1]?.focus();
      }
    }
    safeURL(value, image = false) {
      if (typeof value !== 'string' || !value.trim()) return null;
      try {
        const url = new URL(value, location.origin);
        if (!['https:', 'http:'].includes(url.protocol)) return null;
        if (url.origin === location.origin) return url.href;
        if (image && url.protocol === 'https:' && url.hostname === 'cdn.shopify.com') return url.href;
      } catch (_) {}
      return null;
    }
    async suggest(query) {
      const sequence = ++this.sequence;
      this.status.textContent = this.dataset.loading;
      try {
        let data = this.cache.get(query);
        if (!data) {
          const endpoint = new URL(this.dataset.suggestUrl, location.origin);
          if (endpoint.origin !== location.origin) throw new Error('Invalid search origin');
          endpoint.pathname = endpoint.pathname.replace(/\.json$/, '') + '.json';
          endpoint.searchParams.set('q', query);
          endpoint.searchParams.set('resources[type]', 'query,product,collection,page,article');
          endpoint.searchParams.set('resources[limit]', this.dataset.limit || '3');
          this.request = new AbortController();
          const response = await fetch(endpoint, { signal: this.request.signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
          if (!response.ok || new URL(response.url).origin !== location.origin) throw new Error('Search unavailable');
          data = await response.json();
          if (sequence !== this.sequence) return;
          if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
          this.cache.set(query, data);
        }
        if (sequence !== this.sequence || !this.search.open || !this.isConnected) return;
        const fragment = document.createDocumentFragment();
        let count = 0;
        const groups = data.resources?.results || {};
        ['queries', 'products', 'collections', 'pages', 'articles'].forEach(type => {
          (groups[type] || []).slice(0, Number(this.dataset.limit) || 3).forEach(result => {
            const url = this.safeURL(result.url);
            const title = result.title || result.text;
            if (!url || !title) return;
            const item = document.createElement('li');
            item.className = 'cv-header__result';
            item.id = `${this.results.id}-${count++}`;
            item.setAttribute('role', 'option');
            item.setAttribute('aria-selected', 'false');
            const link = document.createElement('a');
            link.href = url;
            const image = this.safeURL(typeof result.image === 'string' ? result.image : result.image?.url, true);
            if (type === 'products' && image) {
              const img = document.createElement('img');
              const imageURL = new URL(image);
              imageURL.searchParams.set('width', '100');
              img.src = imageURL.href; img.alt = ''; img.width = 44; img.height = 52; img.loading = 'lazy';
              link.append(img);
            }
            const text = document.createElement('span');
            text.textContent = title;
            link.append(text); item.append(link); fragment.append(item);
          });
        });
        this.results.replaceChildren(fragment);
        this.results.hidden = !count;
        this.input.setAttribute('aria-expanded', String(Boolean(count)));
        this.status.textContent = count ? this.dataset.results.replace('[count]', String(count)) : this.dataset.empty;
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === this.sequence) this.status.textContent = this.dataset.error;
      }
    }
    disconnectedCallback() {
      this.close();
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      this.controller = null;
    }
  }
  customElements.define('celvian-desktop-header', CelvianDesktopHeader);
})();
